import styleVars from "./styling/variables.scss";
import { section, urlParams } from "./global-interfaces";
import { SECTION, CATEGORY, CARD, SECTION_MAPPING } from "./constants";

export const checkIfMobile = () =>
  window.innerWidth <= +styleVars.mobileBreakpoint.slice(0, -2);

export const checkIfIOSDevice = () => navigator.userAgent.match(/ipad|iphone/i);

// ref: https://developer.mozilla.org/en-US/docs/Web/API/Window/navigator
export const checkBrowser = () => {
  let sBrowser,
    sUsrAg = navigator.userAgent;

  if (sUsrAg.indexOf("Firefox") > -1) {
    sBrowser = "Mozilla Firefox";
    // "Mozilla/5.0 (X11; Ubuntu; Linux x86_64; rv:61.0) Gecko/20100101 Firefox/61.0"
  } else if (sUsrAg.indexOf("Opera") > -1 || sUsrAg.indexOf("OPR") > -1) {
    sBrowser = "Opera";
    //"Mozilla/5.0 (Macintosh; Intel Mac OS X 10_14_0) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/70.0.3538.102 Safari/537.36 OPR/57.0.3098.106"
  } else if (sUsrAg.indexOf("Trident") > -1) {
    sBrowser = "Microsoft Internet Explorer";
    // "Mozilla/5.0 (Windows NT 10.0; WOW64; Trident/7.0; .NET4.0C; .NET4.0E; Zoom 3.6.0; wbx 1.0.0; rv:11.0) like Gecko"
  } else if (sUsrAg.indexOf("Edge") > -1) {
    sBrowser = "Microsoft Edge";
    // "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.36 Edge/16.16299"
  } else if (sUsrAg.indexOf("Chrome") > -1) {
    sBrowser = "Google Chrome or Chromium";
    // "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Ubuntu Chromium/66.0.3359.181 Chrome/66.0.3359.181 Safari/537.36"
  } else if (sUsrAg.indexOf("Safari") > -1) {
    sBrowser = "Apple Safari";
    // "Mozilla/5.0 (iPhone; CPU iPhone OS 11_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/11.0 Mobile/15E148 Safari/604.1 980x1306"
  } else {
    sBrowser = "unknown";
  }
  return sBrowser;
};

export const getURLParams = (): urlParams => {
  const url = new URL(window.location.href);
  let categories = ["", ""];
  try {
    // if not valid URI, `decodeURI` will throw appwide error
    categories = decodeURI(url.searchParams.get(CATEGORY) as string).split(",");
  } catch (e) {
    console.log("e", e); //e: "malformed URI"
  }

  return {
    [SECTION]:
    // validate section: if section is invalid, replaces with first section
      SECTION_MAPPING.find(d => d.hash === url.searchParams.get(SECTION)) ||
      SECTION_MAPPING[0],
    // append params to create a three-position array [0, 1, 2]
    [CATEGORY]: [
      "", // the 0th place is always null
      ...categories,
    ],
    [CARD]: +url.hash.replace("#", ""),
  };
};

// ref: https://stackoverflow.com/a/50861534
export const generateURL = (
  section: section | undefined,
  card?: number,
  categories?: (string | null)[]
) => {
  let params = new URLSearchParams(window.location.search);
  if (section) params.set(SECTION, section.hash);
  if (categories)
    // only encode the relevant categories in url [0, 1, 2]; [0] always null, so slice off [1, 2]
    params.set(CATEGORY, encodeURI(categories.slice(1).toString()));
  else params.delete(CATEGORY); // clean out categories lingering in url

  const hash = card ? "#" + card : "";

  return (
    window.location.protocol +
    "//" +
    window.location.host +
    window.location.pathname +
    "?" +
    params.toString() +
    hash
  );
};

export const updateURL = (section: section) => {
  if (history.pushState) {
    const newURL = generateURL(section);
    window.history.pushState({ path: newURL }, "", newURL);
  }
};

export /**
 * returns an ID of the form 'sectionHash-#'
 * @param sectionTitle
 * @param card numnber
 */
const generateCardID = (section: section, card: number) =>
  section.hash + "-" + card;

export const parseCardID = (id: string): [string, string] => {
  const [sectionHash, cardString] = id.split(/(-\d)+/, 2);
  return [sectionHash, cardString.replace("-", "")];
};

export const scrollToElement = (
  section: section,
  card?: number | null,
  wrapperQuery: string = ".sections-wrapper"
) => {
  let query =
    "#" + (card && card > 0 ? generateCardID(section, card) : section.hash);
  let el = document.querySelector(query) as HTMLElement;

  if (!el) {
    // case in which card number exists, but no DOM element matches it
    query = "#" + section.hash;
    el = document.querySelector(query) as HTMLElement;
    updateURL(section); // clean out URL
  }

  /** if scrolling to a Card, need to offset
   * the scroll by the sticky header height */
  const isCard = el.classList.contains("Card");
  const titleHeightBuffer = isCard
    ? ((el.parentNode as HTMLElement).querySelector(
        ".section-title"
      ) as HTMLElement).offsetHeight
    : 0;

  const wrapper = document.querySelector(wrapperQuery);

  if (el && wrapper) {
    wrapper.scrollTo({
      behavior: "smooth",
      top:
        el.offsetTop +
        (el.offsetParent as HTMLElement).offsetTop -
        titleHeightBuffer,
    });
  }
};

/** generates an array of [start, end] for
 * each emphasized section in an annotation. */
export const getIndicesOf = (str: string, char: string): number[][] =>
  Array.from(str)
    .reduce<number[]>((a, c, i) => (c === char ? [...a, i] : a), [])
    .reduce<number[][]>( // group into pairs of [start, end]
      (a, c, i, array) => (i % 2 === 0 ? [...a, [array[i], array[i + 1]]] : a),
      []
    );

export const truncator = (str: string, max: number) => {
  let maxClean = Math.round(max);
  const result: string =
    str.length <= maxClean || str.indexOf(" ") > maxClean || !~str.indexOf(" ")
      ? str
      : str[maxClean] === " "
      ? str.slice(0, maxClean) + "..."
      : truncator(str.slice(0, maxClean), maxClean - 1);
  return result;
};
